import { mapActions, mapGetters } from 'vuex'
// selectedGroupId, selectedSeasonId
import { Workspace } from '@/mixins/workspace'
import { isEmpty } from 'lodash'

export const Filters = {
  mixins: [Workspace],

  data () {
    return {
      hasTrackedDeprecatedFilterFormat: false
    }
  },

  computed: {
    ...mapGetters([
      'getStyleMetaFilters',
      'getStyleHubFilters',
      'getOptionMetaFilters',
      'getCustomTags'
    ]),

    /**
     * @returns {object}
     */
    customTags () {
      return this.getCustomTags(this.selectedGroupId, this.selectedSeasonId)
    },

    /**
     * @returns {object}
     */
    styleMetaFilters () {
      return this.getStyleMetaFilters
    },

    /**
     * @returns {object}
     */
    styleHubFilters () {
      return this.getStyleHubFilters
    },

    /**
     * @returns {object}
     */
    optionMetaFilters () {
      return this.getOptionMetaFilters
    }
  },

  methods: {
    ...mapActions([
      'fetchOptionMetaFilters',
      'fetchStyleMetaFilters',
      'fetchStyleHubFilters',
      'fetchCustomTags'
    ]),

    /**
     */
    async fetchFilters () {
      await Promise.all([
        this.fetchTagByGroupAndSeason(
          this.selectedGroupId,
          this.selectedSeasonId
        ),
        this.fetchOptionMetaFilters(),
        this.fetchStyleMetaFilters(),
        this.fetchStyleHubFilters()
      ])
    },

    /**
     * @param {number} groupId
     * @param {number} seasonId
     */
    async fetchTagByGroupAndSeason (groupId, seasonId) {
      if (groupId && seasonId) {
        await this.fetchCustomTags({
          groupId: this.selectedGroupId,
          seasonId: this.selectedSeasonId,
          assignedOnly: true
        })
      }
    },

    /**
     * @param   {object[]} appliedFilters
     * @param   {boolean}  displayOptions
     *
     * @returns {object[]}
     */
    parseAppliedFilters (appliedFilters, displayOptions) {
      const newAppliedFilters = []

      appliedFilters.forEach(filter => {
        const filterDeprecatedParsed = this.parseDeprecatedAppliedFilter(filter)

        if (filterDeprecatedParsed) {
          const keyProcessed = this.getKeyProcessed(
            Object.keys(filterDeprecatedParsed)[0]
              .replace('style__', '')
              .replace('options__', ''),
            displayOptions
          )

          newAppliedFilters.push({
            [keyProcessed]: Object.values(filterDeprecatedParsed)[0].toString()
          })
        }
      })

      this.hasTrackedDeprecatedFilterFormat = false

      return newAppliedFilters
    },

    /**
     * Parses the old url parameters to the new format.
     * e.g. styleMeta__Product=Pants to product_group_id=12
     * Temporary translation service to keep until old urls are not used anymore.
     *
     * @param   {object}        filter
     *
     * @returns {object | null}
     */
    parseDeprecatedAppliedFilter (filter) {
      const keyOldFormat = Object.keys(filter)[0]
      const keyProcessed = keyOldFormat
        .replace('styleMeta__', '')
        .replace('optionMeta__', '')

      if (keyProcessed === keyOldFormat) {
        return filter
      }

      if (!this.hasTrackedDeprecatedFilterFormat) {
        this.trackDeprecatedFilterFormat()
        this.hasTrackedDeprecatedFilterFormat = true
      }

      const valueOldFormat = Object.values(filter)[0]
      const metaFilterEntries = [
        ...Object.entries(this.styleMetaFilters),
        ...Object.entries(this.optionMetaFilters)
      ]

      for (const [key, { name, values }] of metaFilterEntries) {
        if (keyProcessed === name) {
          const valueNewFormat = values.find(
            value => value.name === valueOldFormat
          )

          if (valueNewFormat) {
            return {
              [key]: valueNewFormat.id
            }
          }
        }
      }

      return null
    },

    /**
     * Sends a GA even if the user reached a collection overview url with styleMeta__ or optionMeta__ parameters.
     */
    trackDeprecatedFilterFormat () {
      this.$tracking.trackEvent({
        category: 'Routing',
        action: 'Access Collection Overview with deprecated filters',
        label: this.$route.fullPath
      })
    },

    /**
     * @param   {boolean} displayOptions
     *
     * @returns {object}
     */
    getCompiledFilters (displayOptions) {
      /* eslint-disable camelcase -- using data from API */
      const { price_id, pattern_id } = this.styleMetaFilters
      const filterGroups = [
        ...(isEmpty(this.styleMetaFilters)
          ? []
          : this.getCompiledMetaFilters(
            { price_id, pattern_id },
            displayOptions
          )),
        ...this.getCompiledMetaFilters(this.optionMetaFilters, displayOptions),
        this.getCompiledCustomTags()
      ]
      /* eslint-enable camelcase -- using data from API */

      const hubFilters = Object.entries(this.styleHubFilters).reduce(
        (acc, current) => {
          const [key, value] = current
          const filterValues = value.values.map(filterValue => {
            return {
              checked: false,
              value: filterValue,
              filter: { [key]: filterValue }
            }
          })

          return { ...acc, ...{ [key]: filterValues } }
        },
        {}
      )

      const compiledFilters = filterGroups.reduce((map, { name, filters }) => {
        map[name] = filters

        return map
      }, hubFilters)

      return Object.keys(compiledFilters)
        .sort()
        .reduce(/**
                 * @param   {object} acc
                 * @param   {string} key
                 *
                 * @returns {object}
                 */
          function (acc, key) {
            acc[key] = compiledFilters[key]

            return acc
          }, {})
    },

    /**
     * @param   {string}  key
     * @param   {boolean} displayOptions
     *
     * @returns {string}
     */
    getKeyProcessed (key, displayOptions) {
      if (Object.prototype.hasOwnProperty.call(this.styleMetaFilters, key)) {
        return displayOptions ? `style__${key}` : key
      } else if (
        Object.prototype.hasOwnProperty.call(this.optionMetaFilters, key)
      ) {
        return displayOptions ? key : `options__${key}`
      } else {
        return key
      }
    },

    /**
     * @param   {object}   metaFilters
     * @param   {boolean}  displayOptions
     *
     * @returns {object[]}
     */
    getCompiledMetaFilters (metaFilters, displayOptions) {
      const filterGroups = []

      Object.entries(metaFilters).forEach(([key, filter]) => {
        const filters = filter.values.map(value => {
          const keyProcessed = this.getKeyProcessed(key, displayOptions)
          const filter = {
            [keyProcessed]: `${value.id}`
          }

          return {
            value: value.name,
            filter: filter,
            checked: false
          }
        })

        filterGroups.push({
          name: filter.name,
          filters
        })
      })

      return filterGroups
    },

    /**
     * @returns {object}
     */
    getCompiledCustomTags () {
      return {
        name: 'Tags',
        filters: this.customTags.map(tag => {
          const filter = {
            tag_id: `${tag.id}`
          }

          return {
            value: `${tag.text}`,
            filter: filter,
            checked: false
          }
        })
      }
    }
  }
}
